<script lang="ts" setup>
    import { init, setValue, updateValue } from "./tom-select";
    import { RecursivePartial, TomSettings } from "tom-select/src/types/index";
    import TomSelectPlugin from "tom-select";
    import {
        computed,
        inject,
        onMounted,
        ref,
        SelectHTMLAttributes,
        useAttrs,
        withDefaults,
    } from "vue";
    import Lucide from "../Lucide/Lucide.vue";
    import Validation from "@/boilerplates/Validation.vue";
    import { twMerge } from "tailwind-merge";
    import ConfirmDialog from "../Dialog/ConfirmDialog.vue";

    export interface TomSelectElement extends HTMLSelectElement {
        TomSelect: TomSelectPlugin;
    }

    export interface TomSelectProps extends SelectHTMLAttributes {
        modelValue: string | string[];
        options?: RecursivePartial<TomSettings>;
        refKey?: string;
        validatedError?: string | string[];
        withValidation?: boolean;
        helperText?: string;
        multiple?: boolean;
        disabled?: boolean;
    }

    export interface TomSelectEmit {
        (e: "update:modelValue", value: string | string[]): void;
        (e: "optionAdd", value: string | number): void;
    }

    export type ProvideTomSelect = (el: TomSelectElement) => void;

    const props = withDefaults(defineProps<TomSelectProps>(), {
        withValidation: false,
    });

    const emit = defineEmits<TomSelectEmit>();
    const attrs = useAttrs();

    const tomSelectRef = ref<TomSelectElement>();
    const showConfirmDialog = ref(false);
    const itemsToDelete = ref<string[]>([]);
    const itemLabels = ref<{[key: string]: string}>({});

    // Compute all default options
    const computedOptions = computed(() => {
        // Basic options
        let options: TomSelectProps["options"] = {
            plugins: {
                dropdown_input: {},
                ...(props.options?.plugins || {}),
            },
            // Use default Tom Select rendering (CSP compliant)
            render: {
                option: function(data: any, escape: any) {
                    return '<div class="option">' + escape(data.text) + '</div>';
                },
                item: function(data: any, escape: any) {
                    return '<div class="item">' + escape(data.text) + '</div>';
                }
            }
        };

        // Add options for multi-select
        if (Array.isArray(props.modelValue)) {
            // Add remove_button plugin (CSP compliant)
            options.plugins = {
                ...options.plugins,
                remove_button: {
                    title: "Remove this item",
                },
            };

            // Set core options
            options = {
                ...options,
                persist: false,
                create: false,
            };
        }

        // Merge with user provided options, but keep our options as priority
        options = {
            ...props.options,
            ...options,
        };

        // Ensure our onDelete handler is applied last to override any existing one
        if (Array.isArray(props.modelValue)) {
            options.onDelete = function(values: string[]) {
                try {
                    console.log('TomSelect onDelete triggered with values:', values);

                    // Store item labels for display in confirmation dialog
                    const clonedElements = document.querySelectorAll(
                        `[data-id='${tomSelectRef.value?.getAttribute("data-id")}'][data-initial-class]`
                    );

                    if (clonedElements.length > 0) {
                        const clonedEl = clonedElements[0] as TomSelectElement;

                        if (clonedEl?.TomSelect) {
                            // Clear previous labels
                            itemLabels.value = {};

                            // Get labels for each value to delete
                            values.forEach(value => {
                                const option = clonedEl.TomSelect.options[value];
                                if (option) {
                                    itemLabels.value[value] = option.text || value;
                                } else {
                                    itemLabels.value[value] = value;
                                }
                            });
                        }
                    }

                    showConfirmDialog.value = true;
                    // Make a copy of the values to ensure we're not affected by reference issues
                    itemsToDelete.value = [...values];
                    return false; // Prevent default deletion
                } catch (error) {
                    console.error('Error in onDelete handler:', error);
                    return false; // Prevent default deletion
                }
            };
        }

        return options;
    });

    const handleConfirmDelete = () => {
        try {
            console.log('Confirm delete triggered');
            console.log('Current model value:', props.modelValue);
            console.log('Items to delete:', itemsToDelete.value);

            if (!Array.isArray(props.modelValue)) {
                console.log('Not an array, skipping delete');
                showConfirmDialog.value = false;
                itemsToDelete.value = [];
                return;
            }

            // Get a clean copy of the current model value
            const currentValue = [...props.modelValue];
            console.log('Current value (copy):', currentValue);

            // Filter out the items to delete
            const newValue = currentValue.filter(
                value => !itemsToDelete.value.includes(value)
            );
            console.log('New value after filtering:', newValue);

            // Find the cloned element to update it directly
            const clonedElements = document.querySelectorAll(
                `[data-id='${tomSelectRef.value?.getAttribute("data-id")}'][data-initial-class]`
            );

            if (clonedElements.length > 0) {
                const clonedEl = clonedElements[0] as TomSelectElement;

                if (clonedEl?.TomSelect) {
                    // Update directly (synchronously)
                    try {
                        clonedEl.TomSelect.clear();

                        if (newValue.length > 0) {
                            clonedEl.TomSelect.setValue(newValue);
                        }
                    } catch (innerError) {
                        console.error('Error updating TomSelect directly:', innerError);
                    }
                }
            }

            // Emit the model update (this is the most important part)
            emit("update:modelValue", newValue);

            // Reset state
            showConfirmDialog.value = false;
            itemsToDelete.value = [];
            itemLabels.value = {};

        } catch (error) {
            console.error('Error in handleConfirmDelete:', error);
            // Close dialog even if there's an error
            showConfirmDialog.value = false;
            itemsToDelete.value = [];
            itemLabels.value = {};
        }
    };

    const handleCancelDelete = () => {
        try {
            showConfirmDialog.value = false;
            setTimeout(() => {
                itemsToDelete.value = [];
            }, 300);
        } catch (error) {
            console.error('Error in handleCancelDelete:', error);
        }
    };

    const vSelectDirective = {
        mounted(el: TomSelectElement) {
            try {
                // Unique attribute
                el.setAttribute(
                    "data-id",
                    "_" + Math.random().toString(36).substr(2, 9)
                );

                // Clone the select element to prevent tom select remove the original element
                const clonedEl = el.cloneNode(true) as TomSelectElement;

                // Save initial classnames
                const classNames = el?.getAttribute("class");
                classNames && clonedEl.setAttribute("data-initial-class", classNames);

                // Hide original element
                el?.parentNode && el?.parentNode.appendChild(clonedEl);
                el.setAttribute("hidden", "true");

                // Initialize tom select
                setValue(clonedEl, props);
                init(el, clonedEl, props, computedOptions.value || {}, emit);
            } catch (error) {
                console.error('Error mounting TomSelect:', error);
            }
        },
        updated(el: TomSelectElement) {
            try {
                // Find the cloned element
                const clonedElements = document.querySelectorAll(
                    `[data-id='${el.getAttribute("data-id")}'][data-initial-class]`
                );

                if (clonedElements.length === 0) {
                    console.warn('TomSelect: No cloned element found for update');
                    return;
                }

                const clonedEl = clonedElements[0] as TomSelectElement;
                const value = props.modelValue;

                // Update the value
                updateValue(el, clonedEl, value, props, computedOptions.value || {}, emit);
            } catch (error) {
                console.error('Error updating TomSelect:', error);
            }
        },
    };

    const bindInstance = (el: TomSelectElement) => {
        if (props.refKey) {
            const bind = inject<ProvideTomSelect>(`bind[${props.refKey}]`);
            if (bind) {
                bind(el);
            }
        }
    };

    onMounted(() => {
        if (tomSelectRef.value) {
            bindInstance(tomSelectRef.value);
        }
    });

    const computedClass = computed(() => {
        return twMerge([
            `tom-select bg-white pt-0.5`,
            props.disabled ? '' :
            `
            focus:ring-2 focus:ring-custom-tertiary focus:ring-opacity-50
            focus:border-custom-tertiary focus:border-opacity-40
            focus-within:ring-2 focus-within:ring-custom-tertiary focus-within:ring-opacity-50
            focus-within:border-custom-tertiary focus-within:border-opacity-40
            hover:ring-2 hover:ring-custom-tertiary hover:ring-opacity-50
            hover:border-custom-tertiary hover:border-opacity-40
            active:ring-2 active:ring-custom-tertiary active:ring-opacity-50
            active:border-custom-tertiary active:border-opacity-40`,
            props.validatedError?.length && "!border-custom-muted-red !text-custom-muted-red",
        ]);
    });

    const getItemDisplayText = computed(() => {
        if (itemsToDelete.value.length === 0) {
            return 'this item';
        } else if (itemsToDelete.value.length === 1) {
            const itemId = itemsToDelete.value[0];
            return `${itemLabels.value[itemId] || itemId}`;
        } else {
            // For multiple items, create a list
            return itemsToDelete.value.map(id =>
                `${itemLabels.value[id] || id}`
            ).join(', ');
        }
    });
</script>

<template>
    <div v-bind="attrs" class="w-full">
        <div>
            <select
                ref="tomSelectRef"
                v-select-directive
                :value="props.modelValue"
                :disabled="props.disabled"
                :multiple="multiple"
                :class="computedClass"
                @change="
                    (event: any) => {
                        emit('update:modelValue', (event.target as HTMLSelectElement).value);
                    }
                "
                >
                <slot></slot>
            </select>
        </div>
        <div v-if="helperText"
            class="mt-1 text-xs text-gray-500">
            {{ helperText }}
        </div>
        <div v-if="withValidation" class="mt-1 h-5">
            <div
                v-if="validatedError?.length"
                class="flex gap-1 items-center text-sm text-custom-muted-red"
            >
                <Lucide icon="TriangleAlert" class="size-4" />
                <span>{{ typeof validatedError === 'string' ?  validatedError : validatedError[0] }}</span>
            </div>
        </div>

        <ConfirmDialog
            :isOpen="showConfirmDialog"
            type="danger"
            :header="itemsToDelete.length > 1 ? `Remove Multiple` : `Remove '${getItemDisplayText}'`"
            :description="itemsToDelete.length > 1
                ? `Are you sure you want to remove these?`
                : `Are you sure you want to remove?`"
            confirmButton="Remove"
            @close="handleCancelDelete"
            @confirm="handleConfirmDelete" />
    </div>
</template>
